import type { StripeRecurringInterval } from '@/features/webinar/types';

// Compte Stripe du client connecté à l'espace (Stripe Connect) : aucun identifiant secret ici.
export interface StripeConnectAccount {
  connected: boolean;
  stripeAccountId: string | null;
  displayName: string | null;
}

export interface StripeConnectedPrice {
  id: string;
  productName: string | null;
  // En plus petite unité de la devise (centimes), comme Stripe.
  unitAmount: number | null;
  currency: string;
  recurringInterval: StripeRecurringInterval | null;
  recurringIntervalCount?: number | null;
}

export interface StripeConnectedPricePage {
  items: StripeConnectedPrice[];
  hasMore: boolean;
  nextCursor: string | null;
}

// Les devises sans décimale (JPY…) n'ont pas de centimes : c'est Intl qui connaît le nombre.
export function formatStripeAmount(
  unitAmount: number | null,
  currency: string,
  locale: string,
): string | null {
  if (unitAmount == null || !currency) return null;
  let formatter: Intl.NumberFormat;
  try {
    formatter = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency.toUpperCase(),
    });
  } catch {
    return null;
  }
  const fractionDigits = formatter.resolvedOptions().maximumFractionDigits ?? 2;
  return formatter.format(unitAmount / 10 ** fractionDigits);
}

type StripeConnectFailure = 'cancelled' | 'chargesDisabled' | 'alreadyConnected' | 'generic';

export type StripeConnectReturn =
  { result: 'connected' } | { result: 'error'; reason: StripeConnectFailure };

// Raisons renvoyées par le callback du back (StripeConnectCallbackController).
const STRIPE_CONNECT_FAILURES: Record<string, StripeConnectFailure> = {
  authorization_denied: 'cancelled',
  charges_disabled: 'chargesDisabled',
  account_already_connected: 'alreadyConnected',
};

// Retour de l'autorisation Stripe sur la page Intégrations : `?stripe=connected`, ou
// `?stripe=error&reason=…` (refus de l'admin, compte qui ne peut pas encore encaisser…).
export function readStripeConnectReturn(params: URLSearchParams): StripeConnectReturn | null {
  const result = params.get('stripe');
  if (result === 'connected') return { result: 'connected' };
  if (result !== 'error') return null;
  const reason = STRIPE_CONNECT_FAILURES[params.get('reason') ?? ''] ?? 'generic';
  return { result: 'error', reason };
}

// Concatène les pages déjà chargées sans doublon, au cas où Stripe renverrait un prix deux fois.
export function mergeStripePrices(
  current: StripeConnectedPrice[],
  next: StripeConnectedPrice[],
): StripeConnectedPrice[] {
  const known = new Set(current.map((price) => price.id));
  return [...current, ...next.filter((price) => !known.has(price.id))];
}

// « Formation · 157,00 € / 2 mois » : la cadence réelle du prix, sans quoi un prix facturé tous
// les 2 mois passerait pour un prix mensuel et fausserait la durée d'un paiement en plusieurs fois.
export function formatStripeConnectedPriceLabel(
  price: StripeConnectedPrice,
  locale: string,
  formatInterval: (interval: StripeRecurringInterval, count: number) => string,
): string {
  const amount = formatStripeAmount(price.unitAmount, price.currency, locale);
  const interval = price.recurringInterval
    ? formatInterval(price.recurringInterval, price.recurringIntervalCount ?? 1)
    : null;
  return [price.productName ?? price.id, [amount, interval].filter(Boolean).join(' / ')]
    .filter(Boolean)
    .join(' · ');
}

// Stripe Connect n'est pas encore activé sur le compte Welya de production : la connexion d'un
// compte client y échouerait. On masque donc la fonctionnalité dans un build de production tant
// que VITE_STRIPE_CONNECT_ENABLED ne vaut pas « true ».
export function isStripeConnectEnabled(
  env: { MODE?: string; VITE_STRIPE_CONNECT_ENABLED?: string } = import.meta.env,
): boolean {
  return env.VITE_STRIPE_CONNECT_ENABLED === 'true' || env.MODE !== 'production';
}
